import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fireCelebration } from "@/lib/celebrate";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HOW_HEARD } from "@/lib/formOptions";
import { SPONSOR_TIERS } from "@/lib/sponsorTiers";
import { CheckCircle2, ArrowRight, Handshake } from "lucide-react";

const initial = {
  selected_tier: "",
  selected_tier_label: "",
  company_name: "",
  company_website: "",
  products_offered: "",
  benefit_to_group: "",
  first_name: "",
  last_name: "",
  contact_title: "",
  email: "",
  phone: "",
  how_heard: "",
  other_info: "",
};

const Field = ({ label, required, children, testid, hint }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="text-sm font-semibold text-[#0A1128]">
      {label} {required && <span className="text-[#0066FF]">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-[#6B7280]">{hint}</p>}
  </div>
);

const SectionTitle = ({ kicker, title }) => (
  <div className="mb-6">
    <div className="text-xs font-bold tracking-[0.2em] text-[#0066FF] uppercase mb-2">{kicker}</div>
    <h3 className="font-display-tight text-2xl text-[#0A1128]">{title}</h3>
  </div>
);

export default function SponsorForm({ initialTier = "" }) {
  const [data, setData] = useState(() => {
    const tier = SPONSOR_TIERS.find((t) => t.slug === initialTier);
    return tier
      ? { ...initial, selected_tier: tier.slug, selected_tier_label: tier.label }
      : initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    if (!initialTier) return;
    const tier = SPONSOR_TIERS.find((t) => t.slug === initialTier);
    if (tier) {
      setData((d) => ({ ...d, selected_tier: tier.slug, selected_tier_label: tier.label }));
    }
  }, [initialTier]);

  const pickTier = (slug) => {
    const tier = SPONSOR_TIERS.find((t) => t.slug === slug);
    if (!tier) return;
    setData((d) => ({ ...d, selected_tier: tier.slug, selected_tier_label: tier.label }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      "selected_tier", "company_name", "products_offered", "benefit_to_group",
      "first_name", "last_name", "email", "phone",
    ];
    const missing = required.filter((k) => !String(data[k] || "").trim());
    if (missing.length) {
      toast.error("Please fill out all required fields.");
      const el = document.querySelector("[data-missing='true']") || document.querySelector("form");
      el && el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sponsor", data);
      setDone(true);
      fireCelebration();
      toast.success("Application received. Watch your inbox.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="sponsor-form-success"
        className="bg-white border-2 border-[#FACC15] rounded-2xl p-10 md:p-14 text-center shadow-[0_25px_60px_-30px_rgba(250,204,21,0.5)]"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FACC15] text-[#0A1128] mb-5">
          <CheckCircle2 size={32} strokeWidth={2.5} />
        </div>
        <h2 className="font-display text-3xl md:text-4xl text-[#0A1128] mb-3">
          Application received.
        </h2>
        <p className="text-base md:text-lg text-[#374151] max-w-xl mx-auto leading-relaxed">
          We&apos;ve got your <strong className="text-[#0A1128]">{data.selected_tier_label}</strong> application on our end.
          Our team will personally review it and get back to you within 2–3 business days to walk through next steps.
        </p>
        <div className="mt-8 inline-flex flex-wrap justify-center gap-3 text-[11px] font-black uppercase tracking-widest text-[#6B7280]">
          <span className="inline-flex items-center gap-1.5 bg-[#FAFAF7] border border-[#E5E7EB] rounded-full px-4 py-2">
            <CheckCircle2 size={14} className="text-emerald-600" /> Confirmation emailed
          </span>
          <span className="inline-flex items-center gap-1.5 bg-[#FAFAF7] border border-[#E5E7EB] rounded-full px-4 py-2">
            <Handshake size={14} className="text-[#0066FF]" /> Response within 2–3 biz days
          </span>
        </div>
      </div>
    );
  }

  const missingTier = !data.selected_tier;

  return (
    <form
      onSubmit={submit}
      data-testid="sponsor-form"
      className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-10 shadow-[0_25px_60px_-40px_rgba(10,17,40,0.4)]"
    >
      {/* Tier picker */}
      <section className="mb-10">
        <SectionTitle kicker="Step 1" title="Pick the tier that fits" />
        <div
          className="grid gap-4 md:grid-cols-3"
          data-missing={missingTier ? "true" : "false"}
        >
          {SPONSOR_TIERS.map((t) => {
            const active = data.selected_tier === t.slug;
            return (
              <button
                type="button"
                key={t.slug}
                data-testid={`sponsor-tier-${t.slug}`}
                onClick={() => pickTier(t.slug)}
                className={`text-left rounded-2xl border-2 p-5 transition-all ${
                  active
                    ? "border-[#FACC15] bg-[#FACC15]/10 shadow-[0_20px_40px_-25px_rgba(250,204,21,0.7)]"
                    : "border-[#E5E7EB] hover:border-[#0066FF]/60 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0066FF]">
                    {t.spotsLabel}
                  </span>
                  {active && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#0A1128] bg-[#FACC15] px-2 py-0.5 rounded-full">
                      <CheckCircle2 size={11} /> Selected
                    </span>
                  )}
                </div>
                <div className="font-display text-2xl text-[#0A1128] leading-tight mb-1">{t.label}</div>
                <div className="font-display-tight text-lg text-[#0066FF] mb-3">{t.price}</div>
                <div className="text-sm text-[#374151] leading-relaxed">{t.tagline}</div>
              </button>
            );
          })}
        </div>
        {missingTier && (
          <p className="text-xs text-[#0066FF] mt-3 font-semibold">
            Select a tier to continue.
          </p>
        )}
      </section>

      {/* Company */}
      <section className="mb-10">
        <SectionTitle kicker="Step 2" title="About your company" />
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Company name" required testid="field-company-name">
            <Input
              data-testid="sponsor-input-company-name"
              value={data.company_name}
              onChange={(e) => set("company_name", e.target.value)}
              placeholder="Acme Swim Co."
              className="h-11"
            />
          </Field>
          <Field label="Website" testid="field-company-website">
            <Input
              data-testid="sponsor-input-company-website"
              value={data.company_website}
              onChange={(e) => set("company_website", e.target.value)}
              placeholder="https://…"
              className="h-11"
            />
          </Field>
        </div>
        <div className="mt-5">
          <Field
            label="What products or services do you offer?"
            required
            testid="field-products"
            hint="Give us enough detail to picture it in front of our instructors."
          >
            <Textarea
              data-testid="sponsor-input-products"
              rows={4}
              value={data.products_offered}
              onChange={(e) => set("products_offered", e.target.value)}
              placeholder="e.g. Kickboards, drag suits, private-lesson CRM software, learn-to-swim curriculum, insurance for swim schools…"
            />
          </Field>
        </div>
        <div className="mt-5">
          <Field
            label="How does this benefit our group of swim instructors?"
            required
            testid="field-benefit"
            hint="Sponsorship is a two-way street. Tell us how you help them grow, save time, or earn more."
          >
            <Textarea
              data-testid="sponsor-input-benefit"
              rows={4}
              value={data.benefit_to_group}
              onChange={(e) => set("benefit_to_group", e.target.value)}
              placeholder="e.g. Our CRM saves solo instructors 5+ hours/week on scheduling and payments…"
            />
          </Field>
        </div>
      </section>

      {/* Contact */}
      <section className="mb-10">
        <SectionTitle kicker="Step 3" title="Your contact info" />
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="First name" required testid="field-first-name">
            <Input
              data-testid="sponsor-input-first-name"
              value={data.first_name}
              onChange={(e) => set("first_name", e.target.value)}
              className="h-11"
            />
          </Field>
          <Field label="Last name" required testid="field-last-name">
            <Input
              data-testid="sponsor-input-last-name"
              value={data.last_name}
              onChange={(e) => set("last_name", e.target.value)}
              className="h-11"
            />
          </Field>
          <Field label="Title / role" testid="field-title">
            <Input
              data-testid="sponsor-input-title"
              value={data.contact_title}
              onChange={(e) => set("contact_title", e.target.value)}
              placeholder="Founder, Head of Marketing, etc."
              className="h-11"
            />
          </Field>
          <Field label="How did you hear about us?" testid="field-how-heard">
            <Select value={data.how_heard || "unset"} onValueChange={(v) => set("how_heard", v === "unset" ? "" : v)}>
              <SelectTrigger className="h-11" data-testid="sponsor-select-how-heard">
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">— None —</SelectItem>
                {HOW_HEARD.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Email" required testid="field-email">
            <Input
              type="email"
              data-testid="sponsor-input-email"
              value={data.email}
              onChange={(e) => set("email", e.target.value)}
              className="h-11"
            />
          </Field>
          <Field label="Phone" required testid="field-phone">
            <Input
              data-testid="sponsor-input-phone"
              value={data.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="h-11"
            />
          </Field>
        </div>
        <div className="mt-5">
          <Field label="Anything else we should know?" testid="field-other">
            <Textarea
              data-testid="sponsor-input-other"
              rows={3}
              value={data.other_info}
              onChange={(e) => set("other_info", e.target.value)}
              placeholder="Timeline questions, custom activation ideas, past events you've sponsored, etc."
            />
          </Field>
        </div>
      </section>

      <div className="pt-6 border-t border-[#F3F4F6] flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-[#6B7280] max-w-md leading-relaxed">
          Submitting this application does not lock you in — we&apos;ll follow up personally to walk through next steps and payment (Title &amp; Supporting) or shipping logistics (Community).
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="sponsor-submit"
          className="btn-shine inline-flex items-center justify-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] disabled:opacity-60 text-[#0A1128] font-black tracking-widest uppercase px-8 py-4 rounded-md transition-colors text-sm w-full sm:w-auto"
        >
          {submitting ? "Sending…" : "Submit sponsor application"} <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}
